import { Fragment, useEffect, useState } from 'react';
import { Form, Field } from 'react-final-form';
import { Grid2, Button, useMediaQuery, Box, Tab, Link } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

import Layout from 'components/Layout';
import Title from 'core/libs/core-ui/components/Title';
import TextInput from 'core/libs/core-ui/components/Input/Text';
import BasicTable from 'core/libs/core-ui/components/Table';
import SearchBox from 'core/libs/core-ui/components/SearchBox';
import PaginationControlled from 'core/libs/core-ui/components/Pagination';
import CircularIndeterminate from 'core/libs/core-ui/components/Progress';
import Dialog from 'core/libs/core-ui/components/Dialog';
import { useDebounce } from 'core/libs/core-ui/utils/useDebounce';
import { DateFormattedUtils } from 'core/libs/core-ui/utils/dateTimeFormatted';
import { MoneyFormattedUtils } from 'core/libs/core-ui/utils/moneyFormatted';
import PaymentPlatform from 'core/libs/types/PaymentPlatform/PaymentPlatform';

import { usePayment } from 'core/contexts/payment';

import { useStyles } from './styles';
import { toast } from 'react-toastify';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { PaymentLinkStatus } from '@utriper/core';

// TODO: MOVE COMPONENT TO CORE UI LIBRARY
const MiniCopyButton = (textToCopy) => {
    const classes = useStyles();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast.info('Copied to Clipboard', { position: 'top-right', theme: 'colored' });

        setTimeout(() => setCopied(false), 2000);
    };

    return <Fragment>{copied ? <CheckIcon className={classes.tableButton} /> : <ContentCopyIcon onClick={handleCopy} className={classes.tableButton} />}</Fragment>;
};

const PaymentsPage = () => {
    const classes = useStyles();
    const isSmall = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const { updatePayment, getPayments, payments, loading, total } = usePayment();

    const [payment, setPayment] = useState(null);
    const [paymentId, setPaymentId] = useState(null);
    const [openForm, setOpenForm] = useState(false);

    const [emailSearch, setEmailSearch] = useState('');
    const [bookingCodeSearch, setBookingCodeSearch] = useState('');
    const emailDebounce = useDebounce(emailSearch, 300);
    const bookingCodeDebounce = useDebounce(bookingCodeSearch, 300);
    const [orderBy, setOrderBy] = useState('ci_created_on');
    const [direction, setDirection] = useState('desc');
    const [page, setPage] = useState(1);
    const [type, setType] = useState('payment');
    const MAX_PAGE = 20;

    useEffect(() => {
        getPayments({
            emailSearch: emailDebounce,
            bookingCodeSearch: bookingCodeDebounce,
            type,
            pager: {
                column: orderBy,
                direction,
                page,
                pageSize: MAX_PAGE,
            },
        });
    }, [page, orderBy, direction, emailDebounce, bookingCodeDebounce, type]);

    const prettifyPayments = (payments = []) => {
        return payments.map((payment) => ({
            ...payment,
            triper: `${payment.firstName} ${payment.lastName}`,
            priceString: `${payment.currency} ${MoneyFormattedUtils(payment.totalPrice)}`,
            paidPriceString: `${payment.currency} ${MoneyFormattedUtils(payment.totalPaidPrice)}`,
            createdOnString: DateFormattedUtils(payment.createdOn),
            updatedOnString: DateFormattedUtils(payment.updatedOn),
            actionButton: payment.statusId === 0 ? renderApprove({ ...payment }) : '',
            paymentPlatformStr: PaymentPlatform.getLabel(payment.paymentPlatformId),
            code: (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ alignContent: 'center' }}>{payment.code}</span>
                    <MiniCopyButton textToCopy={payment?.code} />
                </div>
            ),
        }));
    };

    const prettifyPaymentLinks = (paymentLinks = []) => {
        return paymentLinks.map((paymentLink) => ({
            ...paymentLink,
            createdOnString: DateFormattedUtils(paymentLink.createdOn),
            updatedOnString: DateFormattedUtils(paymentLink.updatedOn),
            expiryDateString: DateFormattedUtils(paymentLink.expiryDate),
            bookingCode: (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ alignContent: 'center' }}>{paymentLink.bookingCode}</span>
                    <MiniCopyButton textToCopy={paymentLink?.bookingCode} />
                </div>
            ),
            paymentLink: (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Link href={`${process.env.REACT_APP_UTRIPER_BUY_URL}/?code=${paymentLink?.accessCode}`} style={{ alignContent: 'center' }}>
                        Payment Link
                    </Link>
                    <MiniCopyButton textToCopy={`${process.env.REACT_APP_UTRIPER_BUY_URL}/?code=${paymentLink?.accessCode}`} />
                </div>
            ),
            status: PaymentLinkStatus.getLabel(paymentLink.status),
            viewed: paymentLink.viewed ? "It was seen" : "It wasn’t seen",
        }));
    };

    const paymentColumns = [
        { align: 'left', val: 'id', label: 'Id', column: 'ci_id' },
        { align: 'left', val: 'code', label: 'Code', column: 'ci_code', style: { minWidth: '100px' } },
        {
            val: 'triper',
            label: 'Triper',
            column: 'ci_first_name',
            style: { minWidth: '200px' },
        },
        { val: 'email', label: 'Email', column: 'ci_email' },
        {
            val: 'phoneNumber',
            label: 'Mobile Phone',
            column: 'ci_mobile_phone',
        },
        {
            val: 'createdOnString',
            label: 'Created On',
            column: 'ci_created_on',
            style: { minWidth: '250px' },
        },
        {
            val: 'updatedOnString',
            label: 'Updated On',
            column: 'ci_updated_on',
            style: { minWidth: '250px' },
        },
        {
            val: 'paymentPlatformStr',
            label: 'Payment Platform',
            column: 'ci_payment_platform_id',
        },
        {
            align: 'center',
            val: 'paymentId',
            label: 'Payment Id',
            column: 'ci_payment_id',
        },
        {
            align: 'left',
            val: 'paymentLinkId',
            label: 'Payment Link Id',
            column: 'ci_payment_link_id',
            style: { minWidth: '150px' },
        },
        {
            align: 'center',
            val: 'paidPriceString',
            label: 'Paid Amount',
            column: 'ci_total_paid_price',
            style: { minWidth: '150px' },
        },
        {
            align: 'center',
            val: 'priceString',
            label: 'Price',
            column: 'ci_total_price',
            style: { minWidth: '150px' },
        },
        {
            align: 'center',
            val: 'statusId',
            label: 'Status ID',
            column: 'ci_status_id',
        },
        {
            align: 'center',
            val: 'status',
            label: 'Status',
            column: 'ci_status',
        },
        {
            align: 'center',
            val: 'actionButton',
            label: ' ',
        },
    ];

    const paymentMobileColumns = [
        { align: 'left', val: 'id', label: 'Id', column: 'ci_id' },
        { val: 'code', label: 'Code', column: 'ci_code', style: { minWidth: '100px' } },
        { val: 'email', label: 'Email', column: 'ci_email' },
        {
            val: 'createdOnString',
            label: 'Created On',
            column: 'ci_created_on',
            style: { minWidth: '250px' },
        },
        {
            val: 'updatedOnString',
            label: 'Updated On',
            column: 'ci_updated_on',
            style: { minWidth: '250px' },
        },
        {
            align: 'center',
            val: 'paymentPlatformId',
            label: 'Platform Id',
            column: 'ci_payment_platform_id',
        },
        {
            align: 'center',
            val: 'paymentId',
            label: 'Payment Id',
            column: 'ci_payment_id',
        },
        {
            align: 'left',
            val: 'paymentLinkId',
            label: 'Payment Link Id',
            column: 'ci_payment_link_id',
            style: { minWidth: '150px' },
        },
        {
            align: 'right',
            val: 'paidPriceString',
            label: 'Paid Amount',
            column: 'ci_total_paid_price',
            style: { minWidth: '150px' },
        },
        {
            align: 'right',
            val: 'priceString',
            label: 'Price',
            column: 'ci_total_price',
            style: { minWidth: '150px' },
        },
        {
            align: 'center',
            val: 'status',
            label: 'Status',
            column: 'ci_status',
        },
        {
            align: 'center',
            val: 'actionButton',
            label: ' ',
        },
    ];

    const paymentLinkColumns = [
        { align: 'left', val: 'id', label: 'Id', column: 'ci_id' },
        { align: 'left', val: 'bookingCode', label: 'Booking Code', column: 'ci_booking_code' },
        { align: 'left', val: 'paymentLink', label: 'Link', column: 'ci_access_code' },
        { align: 'left', val: 'email', label: 'Email', column: 'ci_contact_email' },
        { align: 'left', val: 'expiryDateString', label: 'expiryDate', column: 'ci_expire_on' },
        { align: 'left', val: 'paymentId', label: 'Payment Id', column: 'ci_payment_id' },
        { align: 'left', val: 'status', label: 'Status', column: 'ci_status' },
        { align: 'left', val: 'viewed', label: 'Viewed', column: 'ci_viewed' },
        {
            val: 'createdOnString',
            label: 'Created On',
            column: 'ci_created_on',
            style: { minWidth: '250px' },
        },
        {
            val: 'updatedOnString',
            label: 'Updated On',
            column: 'ci_updated_on',
            style: { minWidth: '250px' },
        },
    ];

    const paymentLinkMobileColumns = [
        { align: 'left', val: 'id', label: 'Id', column: 'ci_id' },
        { align: 'left', val: 'bookingCode', label: 'Booking Code', column: 'ci_booking_code' },
        { align: 'left', val: 'paymentLink', label: 'Link', column: 'ci_access_code' },
        { align: 'left', val: 'email', label: 'Email', column: 'ci_contact_email' },
        { align: 'left', val: 'paymentId', label: 'Payment Id', column: 'ci_payment_id' },
        { align: 'left', val: 'status', label: 'Status', column: 'ci_status' },
        { align: 'left', val: 'viewed', label: 'Viewed', column: 'ci_viewed' },
        {
            val: 'createdOnString',
            label: 'Created On',
            column: 'ci_created_on',
            style: { minWidth: '250px' },
        },
        {
            val: 'updatedOnString',
            label: 'Updated On',
            column: 'ci_updated_on',
            style: { minWidth: '250px' },
        },
    ];

    const handleSort = (column) => {
        if (orderBy === column) {
            setDirection(direction === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(column);
            setDirection('asc');
        }
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleTypeChange = (event, newValue) => {
        setType(newValue);
        setPage(1);
    };

    const onChangeEmailSearch = (event) => {
        setEmailSearch(event.target.value);
    };

    const onChangeBookingCodeSearch = (event) => {
        setBookingCodeSearch(event.target.value);
    };

    const onSubmitConfirmPayment = async (e) => {
        // TODO FIXME We need to use status lib
        if (!paymentId) {
            return;
        }
        await updatePayment({
            id: payment.id,
            paymentId,
            status: 'approved',
            statusId: 3,
        });
        setPayment(null);
        setOpenForm(false);
        getPayments({
            emailSearch: emailDebounce,
            bookingCodeSearch: bookingCodeDebounce,
            pager: {
                column: orderBy,
                direction,
                page,
                pageSize: MAX_PAGE,
            },
        });
    };

    const renderApprove = (payment) => {
        return (
            <Button
                className={classes.approveButton}
                onClick={() => {
                    setPayment(payment);
                    setOpenForm(true);
                }}>
                Approve
            </Button>
        );
    };

    const renderPaymentDialog = () => {
        return (
            <div>
                <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 8, md: 8, lg: 8, xl: 8 }}> Monton Total: </Grid2>
                    <Grid2 container justifyContent="flex-end" size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
                        {' '}
                        COP$ {MoneyFormattedUtils(payment?.totalPrice)}{' '}
                    </Grid2>
                </Grid2>
                <Grid2 container style={{ marginTop: '10px', marginBottom: '10px' }}>
                    <Grid2 size={{ xs: 12, sm: 8, md: 8, lg: 8, xl: 8 }}> Monto cobrado (reserva de cupo): </Grid2>
                    <Grid2 container justifyContent="flex-end" size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
                        {' '}
                        COP$ {MoneyFormattedUtils(payment?.totalPaidPrice)}{' '}
                    </Grid2>
                </Grid2>
                <div>
                    <Grid2 container>
                        <Grid2 size={12}>
                            <Form
                                onSubmit={() => {}}
                                // initialValues={{ ...values }}
                                render={({ handleSubmit, values }) => (
                                    <form
                                        onSubmit={handleSubmit}
                                        // noValidate
                                        style={isSmall ? { width: '80vw' } : { width: 'auto' }}>
                                        <Field
                                            required
                                            // validate={required}
                                            className="add-experience__capacity"
                                            variant="outlined"
                                            label={'Código único (Datáfono, MP, Paypal)'}
                                            name="paymentId"
                                            value={''}
                                            onTextFieldChange={(e) => {
                                                const { name, value } = e.target;
                                                setPaymentId(value);
                                            }}
                                            component={TextInput}
                                        />
                                    </form>
                                )}
                            />
                        </Grid2>
                    </Grid2>
                </div>
            </div>
        );
    };

    return (
        <Layout contentClassName={classes.content}>
            <Title value={'Payments'} />
            <div style={{ marginBottom: '25px' }}>
                <SearchBox onChange={onChangeEmailSearch} value={emailSearch} label={'Email'} style={{ width: '14rem', marginRight: '1rem' }} />
                <SearchBox onChange={onChangeBookingCodeSearch} value={bookingCodeSearch} label={'Code'} style={{ width: '5rem' }} />
            </div>
            <div>
                {loading ? (
                    <CircularIndeterminate />
                ) : (
                    <>
                        <TabContext value={type}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <TabList onChange={handleTypeChange} aria-label="lab API tabs example">
                                    <Tab label="Payments" value="payment" />
                                    <Tab label="Payment Links" value="paymentLink" />
                                </TabList>
                            </Box>
                            <TabPanel value="payment">
                                <BasicTable size="small" columns={!isSmall ? paymentColumns : paymentMobileColumns} handleSort={handleSort} orderBy={orderBy} direction={direction} values={prettifyPayments(payments)} />
                            </TabPanel>
                            <TabPanel value="paymentLink">
                                <BasicTable size="small" columns={!isSmall ? paymentLinkColumns : paymentLinkMobileColumns} handleSort={handleSort} orderBy={orderBy} direction={direction} values={prettifyPaymentLinks(payments)} />
                            </TabPanel>
                        </TabContext>
                        <br />
                        <Grid2 container justifyContent="center" alignItems="center" width="100%" pb={4}>
                            {total / MAX_PAGE < 1 ? null : <PaginationControlled count={Math.ceil(total / MAX_PAGE)} page={page} onChange={handlePageChange} />}
                        </Grid2>
                    </>
                )}
            </div>
            <Dialog
                open={openForm}
                dialogTitle={'Booking Payment'}
                dialogContent={renderPaymentDialog()}
                handleClose={() => {
                    // DELETE BOOKING!!!
                    // setExpToDelete(null);
                    // onDismissConfirmPayment();
                    setPayment(null);
                    setOpenForm(false);
                }}
                handleSubmit={onSubmitConfirmPayment}
                labelBack="Cancel"
                labelNext="Confirm"
            />
        </Layout>
    );
};

PaymentsPage.displayName = 'PaymentsPage';

export default PaymentsPage;
